import { useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { useBookmarks } from '../hooks/useBookmarks';
import { useMutes, useShowMuted } from '../hooks/useMutes';
import { useDashboardData } from '../hooks/useDashboardData';
import { CATEGORIES, CATEGORY_KANBAN_ORDER, COLORS, IMPACT_ORDER } from '../constants';
import { hasSourceWarning, hasStaleSource } from '../feedSources';
import { compareActivityDesc } from '../itemDates';
import { S } from '../strings';
import type { Category, ParsedItem, UpdateType } from '../types';
import { CategoryKanban, type CategoryGroup } from './CategoryKanban';
import { CommunityView } from './CommunityView';
import { DashboardFilters } from './DashboardFilters';
import { DashboardHeader, type ViewMode } from './DashboardHeader';
import { DataSourcesPanel } from './DataSourcesPanel';
import { DetailPanel } from './DetailPanel';
import { HeroSplit } from './HeroSplit';
import { PriorityListView } from './PriorityListView';
import { Roadmap } from './Roadmap';
import { SingleCategoryView } from './SingleCategoryView';
import { StatCard } from './StatCard';

type CategoryFilter = Category | 'Kaikki';

export function Dashboard() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('Kaikki');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<UpdateType | null>(null);
  const [sortMode, setSortMode] = useState<'newest' | 'upcoming'>('newest');
  const [viewMode, setViewMode] = useState<ViewMode>('kategoriat');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [panelItem, setPanelItem] = useState<ParsedItem | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const { displayName, signOut } = useAuth();
  const { isBookmarked, toggle: toggleBookmark, asItems: bookmarkItems } = useBookmarks();
  const { ids: mutedIds, isMuted, toggle: toggleMute } = useMutes();
  const [showMuted, toggleShowMuted] = useShowMuted();
  const { data, community, sources, loading, error, lastRefresh, isLive, refresh, dismissError } =
    useDashboardData();
  const isCommunityView = viewMode === 'yhteiso';

  // Bookmarks split by kind: community snapshots belong to the Yhteisö tab
  // only. A snapshot without kind (saved before the field existed) is official.
  const { officialBookmarks, communityBookmarks } = useMemo(() => {
    const items = bookmarkItems();
    return {
      officialBookmarks: items.filter((i) => i.kind !== 'community'),
      communityBookmarks: items.filter((i) => i.kind === 'community'),
    };
  }, [bookmarkItems]);

  // Central mute filter. Every list below — views, KPI tiles, category and
  // type counts, the bookmarks-only list and its count — derives from these
  // two, never from `data` / `bookmarkItems()` directly, so no view can show
  // a muted item unless "Näytä kaikki" is on (then it renders dimmed via
  // isMuted). The raw `data` is still used for the empty/loading screen so
  // muting everything doesn't masquerade as "no data yet".
  const visibleData = useMemo(
    () => (showMuted ? data : data.filter((i) => !mutedIds.has(i.id))),
    [data, showMuted, mutedIds],
  );
  const visibleBookmarkItems = useMemo(
    () => (showMuted ? officialBookmarks : officialBookmarks.filter((i) => !mutedIds.has(i.id))),
    [officialBookmarks, showMuted, mutedIds],
  );
  // Same mute filter for the Yhteisö tab, kept apart from the official lists.
  const visibleCommunity = useMemo(
    () => (showMuted ? community : community.filter((i) => !mutedIds.has(i.id))),
    [community, showMuted, mutedIds],
  );
  const visibleCommunityBookmarks = useMemo(
    () =>
      showMuted ? communityBookmarks : communityBookmarks.filter((i) => !mutedIds.has(i.id)),
    [communityBookmarks, showMuted, mutedIds],
  );
  // Muted items in the current tab's data set (official views or Yhteisö) —
  // stale mutes for items that have left the /api/updates window don't count
  // towards the header tooltip.
  const mutedCount = useMemo(
    () =>
      (isCommunityView ? community : data).reduce((n, i) => (mutedIds.has(i.id) ? n + 1 : n), 0),
    [isCommunityView, community, data, mutedIds],
  );
  // Header warning dot: an enabled feed's last run failed, or it has gone
  // stale in the raw loaded data (no item within STALE_AFTER_DAYS).
  const dataSourcesWarning = useMemo(
    () => hasSourceWarning(sources) || hasStaleSource(sources, data),
    [sources, data],
  );

  // Category click resets the dependent sub-selections. Kept in the shell
  // so DashboardFilters can stay a dumb controlled component — only the
  // owner of selectedSubcategory / selectedType should know to clear them.
  const handleSelectCategory = (cat: CategoryFilter) => {
    setSelectedCategory(cat);
    setSelectedSubcategory(null);
    setSelectedType(null);
  };

  const filtered = useMemo(() => {
    // When the bookmarks-only filter is on, render straight from the stored
    // bookmark snapshots — that way bookmarks survive the 180-day data window
    // AND it doesn't matter if the bookmark's stored item-id matches a row
    // currently in `data` (older bookmarks were saved with random ids).
    let items = showBookmarksOnly ? visibleBookmarkItems : visibleData;
    if (selectedCategory !== 'Kaikki') items = items.filter((i) => i.category === selectedCategory);
    if (selectedSubcategory) items = items.filter((i) => i.subcategory === selectedSubcategory);
    if (selectedType) items = items.filter((i) => i.updateType === selectedType);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) || i.summaryFI.toLowerCase().includes(q),
      );
    }
    return items;
  }, [visibleData, showBookmarksOnly, visibleBookmarkItems, selectedCategory, selectedSubcategory, selectedType, searchQuery]);

  const stats = useMemo(
    () => ({
      total: visibleData.length,
      critical: visibleData.filter((i) => i.impact === 'critical').length,
      high: visibleData.filter((i) => i.impact === 'high').length,
      actions: visibleData.filter((i) => i.actionNeeded).length,
      ga: visibleData.filter((i) => i.updateType === 'ga').length,
      deprecation: visibleData.filter((i) => i.updateType === 'deprecation').length,
    }),
    [visibleData],
  );

  const categoryGroups: CategoryGroup[] = useMemo(() => {
    const compare = sortMode === 'upcoming' ? compareUpcoming : compareNewest;
    return CATEGORY_KANBAN_ORDER.map((cat) => ({
      cat,
      label: CATEGORIES.find((c) => c.value === cat)?.label ?? cat,
      color: COLORS.category[cat],
      items: filtered.filter((i) => i.category === cat).sort(compare),
    })).filter((g) => g.items.length > 0);
  }, [filtered, sortMode]);

  const prioritySorted = useMemo(
    () => [...filtered].sort((a, b) => IMPACT_ORDER[a.impact] - IMPACT_ORDER[b.impact]),
    [filtered],
  );

  // --- Empty / loading initial state ---
  if (data.length === 0 && !loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 40,
          textAlign: 'center',
        }}
      >
        <div className="pulse" style={{ fontSize: 64, marginBottom: 20 }}>📡</div>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 10 }}>{S.appTitle}</h1>
        {error ? (
          <div
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 10,
              padding: '12px 20px',
              fontSize: 13,
              color: '#991B1B',
              marginBottom: 20,
              maxWidth: 500,
            }}
          >
            {error}
            <button
              onClick={refresh}
              style={{
                display: 'block',
                marginTop: 12,
                padding: '8px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#0078D4',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {S.tryAgain}
            </button>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 30 }}>
            {S.dashboardPopulatesWhen}
          </p>
        )}
      </div>
    );
  }

  // --- Main layout ---
  const catItems =
    selectedCategory !== 'Kaikki' ? visibleData.filter((i) => i.category === selectedCategory) : [];

  return (
    <div>
      <DashboardHeader
        displayName={displayName}
        signOut={signOut}
        isLive={isLive}
        loading={loading}
        lastRefresh={lastRefresh}
        // Per tab: the Yhteisö tab counts its own visible (mute-filtered) posts.
        dataCount={isCommunityView ? visibleCommunity.length : visibleData.length}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onRefresh={refresh}
        showMuted={showMuted}
        mutedCount={mutedCount}
        onToggleShowMuted={toggleShowMuted}
        onOpenDataSources={() => setSourcesOpen(true)}
        dataSourcesWarning={dataSourcesWarning}
      />

      {error && (
        <div
          style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 10,
            margin: '16px 28px 0',
            padding: '12px 18px',
            fontSize: 13,
            color: '#991B1B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>⚠️ {error}</span>
          <button
            onClick={dismissError}
            style={{
              background: 'none',
              border: 'none',
              color: '#991B1B',
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ padding: '20px 28px' }}>
        {/* KPI tiles, hero and filters are official-only; the Yhteisö tab
            renders CommunityView below instead. */}
        {!isCommunityView && (
          <>
            <div
              className="stats-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 12,
                marginBottom: 22,
              }}
            >
              <StatCard value={stats.total} label={S.stats.total} color="#0078D4" />
              <StatCard value={stats.critical} label={S.stats.critical} color="#D13438" />
              <StatCard value={stats.high} label={S.stats.high} color="#F7630C" />
              <StatCard value={stats.actions} label={S.stats.actions} color="#8B5CF6" icon="⚡" />
              <StatCard value={stats.ga} label={S.stats.ga} color="#107C10" />
              <StatCard value={stats.deprecation} label={S.stats.deprecation} color="#A80000" />
            </div>

            <HeroSplit items={visibleData} onItemClick={setPanelItem} isMuted={isMuted} />

            <DashboardFilters
              data={visibleData}
              bookmarkCount={visibleBookmarkItems.length}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              selectedType={selectedType}
              showBookmarksOnly={showBookmarksOnly}
              onSelectCategory={handleSelectCategory}
              onSelectType={setSelectedType}
              onToggleBookmarksOnly={() => setShowBookmarksOnly((v) => !v)}
            />
          </>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-muted)' }}>
            <div className="pulse" style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <div>{S.loadingDataverse}</div>
          </div>
        )}

        {!loading && viewMode === 'kategoriat' && selectedCategory === 'Kaikki' && (
          <>
            <SortToggle sortMode={sortMode} onChange={setSortMode} />
            <CategoryKanban
              categoryGroups={categoryGroups}
              sortMode={sortMode}
              onSelectCategory={handleSelectCategory}
              isBookmarked={isBookmarked}
              onToggleBookmark={(item) => void toggleBookmark(item)}
              isMuted={isMuted}
              onToggleMute={(item) => void toggleMute(item)}
            />
          </>
        )}

        {!loading && viewMode === 'kategoriat' && selectedCategory !== 'Kaikki' && (
          <SingleCategoryView
            category={selectedCategory as Category}
            subcategory={selectedSubcategory}
            onSelectSubcategory={setSelectedSubcategory}
            catItems={catItems}
            items={filtered}
            isBookmarked={isBookmarked}
            onToggleBookmark={(item) => void toggleBookmark(item)}
            isMuted={isMuted}
            onToggleMute={(item) => void toggleMute(item)}
          />
        )}

        {!loading && viewMode === 'priorisoitu' && (
          <PriorityListView
            items={prioritySorted}
            isBookmarked={isBookmarked}
            onToggleBookmark={(item) => void toggleBookmark(item)}
            isMuted={isMuted}
            onToggleMute={(item) => void toggleMute(item)}
          />
        )}

        {!loading && viewMode === 'roadmap' && (
          <Roadmap items={filtered} onItemClick={setPanelItem} isMuted={isMuted} />
        )}

        {!loading && isCommunityView && (
          <CommunityView
            items={visibleCommunity}
            bookmarkItems={visibleCommunityBookmarks}
            hasAny={community.length > 0 || communityBookmarks.length > 0}
            searchQuery={searchQuery}
            isBookmarked={isBookmarked}
            onToggleBookmark={(item) => void toggleBookmark(item)}
            isMuted={isMuted}
            onToggleMute={(item) => void toggleMute(item)}
          />
        )}
      </div>

      <div
        style={{
          padding: '16px 28px',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 11,
          borderTop: '1px solid var(--border)',
        }}
      >
        {S.company} © {new Date().getFullYear()} — {S.footerPowered}
      </div>

      <DetailPanel
        item={panelItem}
        isBookmarked={isBookmarked}
        onToggleBookmark={(item) => void toggleBookmark(item)}
        isMuted={isMuted}
        onToggleMute={(item) => void toggleMute(item)}
        onClose={() => setPanelItem(null)}
      />

      {/* Raw `data` / `community` on purpose: per-feed counts show ingestion
          health, so they ignore mutes and the bookmarks-only filter. Note:
          official counts still exclude `CVE-` titles, because src/api.ts
          fetchUpdates drops those before the rows ever reach SPA state. */}
      <DataSourcesPanel
        open={sourcesOpen}
        items={data}
        communityItems={community}
        sources={sources}
        onClose={() => setSourcesOpen(false)}
      />
    </div>
  );
}

function SortToggle({
  sortMode,
  onChange,
}: {
  sortMode: 'newest' | 'upcoming';
  onChange: (m: 'newest' | 'upcoming') => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        fontSize: 12,
      }}
    >
      <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
        {S.sort.label}
      </span>
      <div
        style={{
          display: 'inline-flex',
          background: 'var(--surface-muted, rgba(148,163,184,0.12))',
          borderRadius: 8,
          padding: 2,
          gap: 2,
        }}
      >
        {(['newest', 'upcoming'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            style={{
              padding: '4px 12px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              background: sortMode === m ? 'var(--card-bg, #fff)' : 'transparent',
              color: sortMode === m ? 'var(--text, #0078D4)' : 'var(--text-muted, #6B7280)',
              boxShadow: sortMode === m ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              fontFamily: 'inherit',
              transition: 'background 0.12s',
            }}
          >
            {m === 'newest' ? S.sort.newest : S.sort.upcoming}
          </button>
        ))}
      </div>
    </div>
  );
}

// Default kanban sort: newest activity first (the later of publishedDate and
// lastChanged, see src/itemDates.ts). What people expect when they open the
// app — "what arrived or changed recently in this category?"
function compareNewest(a: ParsedItem, b: ParsedItem): number {
  return compareActivityDesc(a, b);
}

// "Tulossa" sort: items with a parsed future effectiveDate bubble to the top,
// soonest first. Past items (and items without a real effective date) fall
// below, most recent activity first. This puts the dated deadlines and
// upcoming rollouts into focus without losing the rest of the stream.
function compareUpcoming(a: ParsedItem, b: ParsedItem): number {
  const now = Date.now();
  const aEff = a.effectiveDate ? new Date(a.effectiveDate).getTime() : NaN;
  const bEff = b.effectiveDate ? new Date(b.effectiveDate).getTime() : NaN;
  const aFuture = !isNaN(aEff) && aEff > now;
  const bFuture = !isNaN(bEff) && bEff > now;
  if (aFuture && !bFuture) return -1;
  if (!aFuture && bFuture) return 1;
  if (aFuture && bFuture) return aEff - bEff; // soonest future first
  return compareActivityDesc(a, b);
}

